import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import moment from "moment";
import { ChevronDown, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// ─── Date/time picker helpers ─────────────────────────────────────────────────
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PURPLE_GRADIENT = "linear-gradient(2deg, #A28FDC 0.74%, #7C3AED 74.61%)";
const PURPLE_SHADOW   = "0 10px 20px 0 rgba(91,76,219,0.30)";
const PURPLE_SOLID    = "#7C3AED";
const PURPLE_LIGHT_BG = "#EDE8FC";

function generateTimeSlots() {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m of [0, 30]) {
      const value = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
      slots.push({ value, label: dayjs(`2000-01-01 ${value}`).format("h:mm A") });
    }
  }
  return slots;
}
const ALL_SLOTS = generateTimeSlots();

function roundToNearest30(t) {
  const mins = t.minute();
  if (mins < 15) return t.minute(0).second(0);
  if (mins < 45) return t.minute(30).second(0);
  return t.add(1, "hour").minute(0).second(0);
}

function buildCalendarDays(month) {
  const start = month.startOf("month");
  const end   = month.endOf("month");
  const days  = [];
  for (let i = 0; i < start.day(); i++) days.push(null);
  for (let d = start; !d.isAfter(end); d = d.add(1, "day")) days.push(d.clone());
  return days;
}


// NOTE: Header/Footer are not imported here — same as UnifiedListing.jsx (File A),
// which relies on the parent `Layout` component (../Layout/Layout) to render
// chrome around the routed page. If you have standalone Header/Footer
// components, import them here and drop them in where noted below.

/**
 * VehicleListing
 * - Functionality (state mgmt, search params, sort, booking flow) ported from UnifiedListing.jsx (File A)
 * - Visual design (header, search bar, card layout, footer) ported from the styled markup (File B)
 * - Data source: GET /api/subscriptionbookings/search-availability
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const VehicleListing = () => {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);

  // --- Search config (from A) ---
  const [cityId, setCityId] = useState(params.get("cityId") || "4");
  const [startDate, setStartDate] = useState(
    params.get("startDate") || dayjs().format("YYYY-MM-DDTHH:mm"),
  );
  const [endDate, setEndDate] = useState(
    params.get("endDate") || dayjs().add(7, "day").format("YYYY-MM-DDTHH:mm"),
  );
  const [totalKms, setTotalKms] = useState(params.get("freeKmValue") || "140");
  const [sortOption, setSortOption] = useState("lowtohigh");
  const [subscriptionDays, setSubscriptionDays] = useState(7);

  const [bufferTime, setBufferTime] = useState(3);
  const [minBookingDays, setMinBookingDays] = useState(7);

  // ─── Date/time picker state ───────────────────────────────────────────────
  const [pickupDate, setPickupDate] = useState(() => {
    const sd = params.get("startDate");
    return sd ? dayjs(sd) : roundToNearest30(dayjs().add(3, "hour"));
  });
  const [pickupTime, setPickupTime] = useState(() => {
    const sd = params.get("startDate");
    return sd ? dayjs(sd).format("HH:mm") : roundToNearest30(dayjs().add(3, "hour")).format("HH:mm");
  });
  const [dropDate, setDropDate] = useState(() => {
    const ed = params.get("endDate");
    if (ed) return dayjs(ed);
    const p = roundToNearest30(dayjs().add(3, "hour"));
    return p.add(7, "day");
  });
  const [dropTime, setDropTime] = useState(() => {
    const ed = params.get("endDate");
    return ed ? dayjs(ed).format("HH:mm") : roundToNearest30(dayjs().add(3, "hour")).format("HH:mm");
  });
  const [activePanel, setActivePanel] = useState(null);
  const [calMonth, setCalMonth]       = useState(() => {
    const sd = params.get("startDate");
    return sd ? dayjs(sd).startOf("month") : dayjs().startOf("month");
  });
  const panelRef = useRef(null);

  // --- Data ---
  const [carData, setCarData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [hubdetails, setHubdetails] = useState([]);
  const [hubId, setHubId] = useState("1");

  useEffect(() => {
    fetch(`${API_BASE}/api/cities`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.statusCode === "0100" && Array.isArray(data?.entities?.rows)) {
          setCities(data.entities.rows.sort((a, b) => a.id - b.id));
        }
      })
      .catch((e) => console.error(e));
  }, []);

  // Hubs + booking config — fetched when city changes
  useEffect(() => {
    if (!cityId) return;
    fetch(`${API_BASE}/api/hubs?limit=100&cityId=${cityId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.statusCode === "0100" && Array.isArray(data?.entities?.rows)) {
          setHubdetails(data.entities.rows);
          setHubId(data.entities.rows[0]?.id || "1");
        } else {
          setHubdetails([]);
        }
      })
      .catch((e) => console.error(e));

    fetch(`${API_BASE}/api/bookingconfigs?cityId=${cityId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.statusCode === "0100" && data.entities?.rows[0]) {
          setBufferTime(data.entities.rows[0].bufferTime || 3);
        }
      })
      .catch((e) => console.error(e));
  }, [cityId]);

  // Sync pickupDate/Time → startDate string
  useEffect(() => {
    if (!pickupDate || !pickupTime) return;
    const [h, m] = pickupTime.split(":").map(Number);
    setStartDate(pickupDate.hour(h).minute(m).second(0).format("YYYY-MM-DDTHH:mm"));
  }, [pickupDate, pickupTime]);

  // Sync dropDate/Time → endDate string
  useEffect(() => {
    if (!dropDate || !dropTime) return;
    const [h, m] = dropTime.split(":").map(Number);
    setEndDate(dropDate.hour(h).minute(m).second(0).format("YYYY-MM-DDTHH:mm"));
  }, [dropDate, dropTime]);

  // Buffer time validation — snap pickupDate/Time forward if too soon after bufferTime loads
  useEffect(() => {
    const earliest = roundToNearest30(dayjs().add(bufferTime, "hour"));
    const [ph, pm] = pickupTime.split(":").map(Number);
    const pickupMoment = pickupDate.hour(ph).minute(pm);
    if (pickupMoment.isBefore(earliest)) {
      setPickupDate(earliest);
      setPickupTime(earliest.format("HH:mm"));
      const drop = earliest.add(subscriptionDays, "day");
      setDropDate(drop);
      setDropTime(earliest.format("HH:mm"));
    }
  }, [bufferTime]);

  const getDisabledPickupTimes = () => {
    const disabled = new Set();
    if (!pickupDate) return disabled;
    if (pickupDate.isSame(dayjs(), "day")) {
      const earliest = roundToNearest30(dayjs().add(bufferTime, "hour"));
      ALL_SLOTS.forEach(({ value }) => {
        const [h, m] = value.split(":").map(Number);
        if (pickupDate.hour(h).minute(m).isBefore(earliest)) disabled.add(value);
      });
    }
    return disabled;
  };

  const getDisabledDropTimes = () => {
    const disabled = new Set();
    if (!pickupDate || !pickupTime || !dropDate) return disabled;
    const [ph, pm] = pickupTime.split(":").map(Number);
    const minDrop = pickupDate.hour(ph).minute(pm).add(subscriptionDays * 24, "hour");
    ALL_SLOTS.forEach(({ value }) => {
      const [dh, dm] = value.split(":").map(Number);
      if (dropDate.hour(dh).minute(dm).isBefore(minDrop)) disabled.add(value);
    });
    return disabled;
  };

  // --- Filters (from A) ---
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));

  const getVehicles = useCallback(async () => {
    setLoading(true);
    const query = `startDate=${startDate}&endDate=${endDate}&cityId=${cityId}`;
    try {
      const res = await fetch(
        `${API_BASE}/api/subscriptionbookings/search-availability?${query}`,
      );
      const data = await res.json();
      if (data?.statusCode === "0100" && Array.isArray(data.dataValues)) {
        setOriginalData(data.dataValues);
      } else {
        setOriginalData([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load vehicles. Please try again.");
      setOriginalData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, totalKms, cityId]);

  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (isInitialLoad.current) {
      getVehicles();
      isInitialLoad.current = false;
    }
  }, [getVehicles]);

  // --- Local filter/sort application (ported from A's applyLocalLogic) ---
  const applyLocalLogic = useCallback((data, filters, sort) => {
    let filtered = Array.isArray(data) ? [...data] : [];
    const keyMap = {
      Seater: "seater",
      "Fuel Type": "fuelTypeText",
      "Transmission Type": "transmissionTypeText",
      "Vehicle Body Type": "bodyTypeText",
    };
    Object.entries(filters).forEach(([title, values]) => {
      if (values.length > 0 && keyMap[title]) {
        filtered = filtered.filter((car) =>
          values.includes(car[keyMap[title]]?.toString()),
        );
      }
    });
    filtered.sort((a, b) => {
      if (a.isSoldOut !== b.isSoldOut) return a.isSoldOut ? 1 : -1;
      return sort === "lowtohigh"
        ? a.costToTheCustomer - b.costToTheCustomer
        : b.costToTheCustomer - a.costToTheCustomer;
    });
    setCarData(filtered);
  }, []);

  useEffect(() => {
    applyLocalLogic(originalData, selectedFilters, sortOption);
  }, [originalData, selectedFilters, sortOption, applyLocalLogic]);

  const handleCheckbox = (title, apiKey) => {
    setSelectedFilters((prev) => {
      const current = prev[title] || [];
      const updated = current.includes(apiKey)
        ? current.filter((v) => v !== apiKey)
        : [...current, apiKey];
      const next = { ...prev, [title]: updated };
      if (updated.length === 0) delete next[title];
      return next;
    });
  };

  // --- Booking handoff (ported from A) ---
  const handleBooking = (car) => {
    const startDateTime = moment(startDate, "YYYY-MM-DDTHH:mm");
    const endDateTime = moment(endDate, "YYYY-MM-DDTHH:mm");
    const diffHours = endDateTime.diff(startDateTime, "hours", true);

    if (diffHours < 24) {
      toast.error("Minimum subscription duration is 7 days");
      return;
    }

    const customerId = localStorage.getItem("id");
    const profilestatus = localStorage.getItem("islogin");

    if (customerId && profilestatus === "true") {
      const queryParams = {
        cityId,
        startDate,
        endDate,
        vehicleId: car?.id,
        source: "website",
        packageId: car?.packageId,
        customerId,
        groupId: car?.vehicleGroupId,
        seating: car?.seater,
        tramissiontype: car?.transmissionTypeText,
        fuelType: car?.fuelTypeText,
        freekm: car?.totalFreeKms,
        extraKm: car?.extraKmRental,
        plankm: totalKms,
        hubid: car?.hubId,
        isSubscription: true,
        bookingType: 716,
      };
      localStorage.setItem("bookingvalues", JSON.stringify(queryParams));
      navigate(`/bookings`);
    } else {
      navigate(`/login?redirect=/bookings`);
    }
  };

  const durationOptions = [
    { label: "7 Days+", sub: "Perfect for week - long journeys", days: 7 },
    { label: "30 Days+", sub: "Better value for longer trips", days: 30 },
  ];

  const applyDuration = (days) => {
    setSubscriptionDays(days);
    const [ph, pm] = pickupTime.split(":").map(Number);
    const newDrop = pickupDate.hour(ph).minute(pm).add(days, "day");
    setDropDate(newDrop);
    setDropTime(newDrop.format("HH:mm"));
  };

  return (
    <div className="w-full min-h-screen bg-[#FFFAF9] font-['Outfit'] pt-16 pb-40">
      {/* ---------- Search bar (design from B; site Header/Footer come from Layout) ---------- */}
      <div className="w-full bg-white shadow-[0px_2px_9px_rgba(0,0,0,0.10)]">
        <div className="w-full py-4 flex flex-col items-center gap-3">
          <div className="w-[95%] max-w-[1311px] h-16 px-3 py-1 bg-white rounded-[32px] shadow-[0px_4px_12px_-2px_rgba(0,0,0,0.04)] border border-gray-200 flex items-center gap-2">
            {/* City Selector */}
            <div className="flex items-center gap-3 px-3 py-2 shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
                <path d="M10.2197 25.4404C10.4743 25.3662 10.748 25.396 10.9805 25.5234C11.213 25.651 11.3857 25.8655 11.46 26.1201C11.5342 26.3747 11.5044 26.6484 11.377 26.8809C11.2653 27.0844 11.0866 27.2413 10.873 27.3271L10.7803 27.3604C10.0069 27.5862 9.42156 27.8316 9.02441 28.0723L8.31543 28.502L9.02637 28.9287C9.43401 29.1736 10.0432 29.4287 10.8506 29.6592C12.639 30.1701 15.1667 30.5 18 30.5C20.8333 30.5 23.361 30.1701 25.1494 29.6592C25.9587 29.4286 26.5661 29.1735 26.9736 28.9287L27.6826 28.5029L26.9766 28.0732C26.5815 27.8328 25.9982 27.5875 25.2285 27.3623H25.2295C24.9789 27.2848 24.7688 27.1118 24.6445 26.8809C24.5203 26.65 24.4913 26.3797 24.5645 26.1279C24.6378 25.8761 24.8073 25.6623 25.0361 25.5342C25.2632 25.4071 25.531 25.3735 25.7822 25.4404C26.7575 25.7253 27.6184 26.0815 28.2617 26.5205V26.5215C28.8721 26.9411 29.5 27.5916 29.5 28.5C29.5 29.4188 28.8562 30.0754 28.2344 30.4961C27.5788 30.9375 26.6982 31.2968 25.6992 31.582H25.6982C23.6876 32.1584 20.9672 32.5 18 32.5C15.0328 32.5 12.3124 32.1584 10.3018 31.582H10.3008C9.42613 31.3323 8.64215 31.0257 8.02051 30.6572L7.76465 30.4961C7.14209 30.0733 6.5 29.418 6.5 28.5C6.5 27.5917 7.12699 26.9401 7.7373 26.5205C8.38111 26.081 9.2434 25.7255 10.2197 25.4404ZM18 3.5C20.8511 3.5 23.5855 4.63242 25.6016 6.64844C27.6176 8.66445 28.75 11.3989 28.75 14.25C28.75 17.9128 26.7523 20.9247 24.6191 23.1084C23.7721 23.9665 22.8622 24.7606 21.8965 25.4824H21.8955C21.467 25.8042 20.7497 26.276 20.1279 26.6738C19.8196 26.8711 19.5387 27.0475 19.335 27.1748C19.2333 27.2383 19.1507 27.2899 19.0938 27.3252C19.0654 27.3428 19.0432 27.357 19.0283 27.3662C19.0209 27.3708 19.0145 27.3736 19.0107 27.376H19.0088C18.7005 27.5487 18.3535 27.6406 18 27.6406C17.644 27.6406 17.2943 27.5481 16.9844 27.373C15.9898 26.7964 15.0269 26.165 14.1025 25.4814H14.1035C13.2577 24.8517 12.4547 24.166 11.7012 23.4287L11.3818 23.1094C9.24834 20.9256 7.25 17.9134 7.25 14.25C7.25 11.3989 8.38242 8.66445 10.3984 6.64844C12.4145 4.63242 15.1489 3.5 18 3.5ZM18 10.75C17.0717 10.75 16.1818 11.119 15.5254 11.7754C14.869 12.4318 14.5 13.3217 14.5 14.25C14.5 15.1783 14.869 16.0682 15.5254 16.7246C16.1818 17.381 17.0717 17.75 18 17.75C18.9283 17.75 19.8182 17.381 20.4746 16.7246C21.131 16.0682 21.5 15.1783 21.5 14.25C21.5 13.3217 21.131 12.4318 20.4746 11.7754C19.8593 11.1601 19.0388 10.7969 18.1738 10.7539L18 10.75Z" fill="#ED2D20" stroke="#53100B"/>
              </svg>
              <div className="flex flex-col items-start">
                <span className="text-[#9CA3AF] text-xs font-normal">City</span>
                <select
                  value={cityId}
                  onChange={(e) => setCityId(e.target.value)}
                  className="text-[#111827] text-sm font-medium bg-transparent outline-none cursor-pointer pr-2"
                >
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Divider */}
            <div className="w-px h-8 bg-gray-200 shrink-0" />

            {/* Hub Selector */}
            <div className="flex items-center gap-3 px-3 py-2 shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M12.75 10.5L33 18V33H12.75V10.5Z" fill="#ED2D20" stroke="#53100B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12.75 10.5L3 18V33H12.75" stroke="#53100B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M26.25 33V24L19.5 21.75V33" stroke="#F9F8F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M33 33H12.75" stroke="#53100B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
              <div className="flex flex-col items-start">
                <span className="text-[#9CA3AF] text-xs font-normal">City Hub</span>
                <select
                  value={hubId}
                  onChange={(e) => setHubId(e.target.value)}
                  disabled={hubdetails.length === 0}
                  className="text-[#111827] text-sm font-medium bg-transparent outline-none cursor-pointer pr-2 max-w-[140px]"
                >
                  {hubdetails.length === 0 ? (
                    <option value="1">Select Hub</option>
                  ) : (
                    hubdetails.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.hubName}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            {/* Divider */}
            <div className="w-px h-8 bg-gray-200 shrink-0" />

            {/* Pick Up + Pill + Drop Off — grid forces pill to be exactly centred */}
            <div className="flex-1 grid grid-cols-[1fr_auto_1fr] items-center gap-32 min-w-0">

            {/* Pick Up Section */}
            <div className="flex items-center gap-2.5 min-w-0 justify-end">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 36 36"
                fill="none"
                className="shrink-0"
              >
                <path
                  d="M16.2127 14.3333C15.8292 14.3333 15.4615 14.4647 15.1903 14.6987C14.9192 14.9327 14.7668 15.2501 14.7668 15.581V15.5976C14.7668 16.2864 15.4146 16.8454 16.2127 16.8454H16.232C16.6154 16.8454 16.9832 16.7139 17.2543 16.4799C17.5255 16.2459 17.6778 15.9285 17.6778 15.5976V15.581C17.6778 15.2501 17.5255 14.9327 17.2543 14.6987C16.9832 14.4647 16.6154 14.3333 16.232 14.3333H16.2127ZM8.50151 20.9878C8.11805 20.9878 7.75029 21.1193 7.47914 21.3533C7.20799 21.5873 7.05566 21.9046 7.05566 22.2355V22.2522C7.05566 22.9409 7.7034 23.4999 8.50151 23.4999H8.52079C8.90425 23.4999 9.27201 23.3685 9.54316 23.1345C9.8143 22.9005 9.96664 22.5831 9.96664 22.2522V22.2355C9.96664 21.9046 9.8143 21.5873 9.54316 21.3533C9.27201 21.1193 8.90425 20.9878 8.52079 20.9878H8.50151ZM12.3571 20.9878C11.9736 20.9878 11.6059 21.1193 11.3347 21.3533C11.0636 21.5873 10.9113 21.9046 10.9113 22.2355V22.2522C10.9113 22.9409 11.559 23.4999 12.3571 23.4999H12.3764C12.7598 23.4999 13.1276 23.3685 13.3987 23.1345C13.6699 22.9005 13.8222 22.5831 13.8222 22.2522V22.2355C13.8222 21.9046 13.6699 21.5873 13.3987 21.3533C13.1276 21.1193 12.7598 20.9878 12.3764 20.9878H12.3571ZM14.7668 22.2355C14.7668 21.9046 14.9192 21.5873 15.1903 21.3533C15.4615 21.1193 15.8292 20.9878 16.2127 20.9878H16.232C16.6154 20.9878 16.9832 21.1193 17.2543 21.3533C17.5255 21.5873 17.6778 21.9046 17.6778 22.2355V22.2522C17.6778 22.5831 17.5255 22.9005 17.2543 23.1345C16.9832 23.3685 16.6154 23.4999 16.232 23.4999H16.2127C15.8292 23.4999 15.4615 23.3685 15.1903 23.1345C14.9192 22.9005 14.7668 22.5831 14.7668 22.2522V22.2355ZM20.0683 17.6605C19.6848 17.6605 19.3171 17.792 19.0459 18.026C18.7748 18.26 18.6224 18.5773 18.6224 18.9083V18.9249C18.6224 19.6137 19.2702 20.1726 20.0683 20.1726H20.0876C20.471 20.1726 20.8388 20.0412 21.1099 19.8072C21.3811 19.5732 21.5334 19.2558 21.5334 18.9249V18.9083C21.5334 18.5773 21.3811 18.26 21.1099 18.026C20.8388 17.792 20.471 17.6605 20.0876 17.6605H20.0683ZM20.0683 20.9878C19.6848 20.9878 19.3171 21.1193 19.0459 21.3533C18.7748 21.5873 18.6224 21.9046 18.6224 22.2355V22.2522C18.6224 22.9409 19.2702 23.4999 20.0683 23.4999H20.0876C20.471 23.4999 20.8388 23.3685 21.1099 23.1345C21.3811 22.9005 21.5334 22.5831 21.5334 22.2522V22.2355C21.5334 21.9046 21.3811 21.5873 21.1099 21.3533C20.8388 21.1193 20.471 20.9878 20.0876 20.9878H20.0683ZM22.478 18.9083C22.478 18.5773 22.6304 18.26 22.9015 18.026C23.1727 17.792 23.5404 17.6605 23.9239 17.6605H23.9432C24.3266 17.6605 24.6944 17.792 24.9655 18.026C25.2367 18.26 25.389 18.5773 25.389 18.9083V18.9249C25.389 19.2558 25.2367 19.5732 24.9655 19.8072C24.6944 20.0412 24.3266 20.1726 23.9432 20.1726H23.9239C23.5404 20.1726 23.1727 20.0412 22.9015 19.8072C22.6304 19.5732 22.478 19.2558 22.478 18.9249V18.9083ZM18.6224 15.5893C18.6224 14.8956 19.274 14.3333 20.0779 14.3333H23.9335C24.3195 14.3333 24.6897 14.4656 24.9627 14.7011C25.2357 14.9367 25.389 15.2562 25.389 15.5893C25.389 15.9224 25.2357 16.2419 24.9627 16.4775C24.6897 16.713 24.3195 16.8454 23.9335 16.8454H20.0779C19.6919 16.8454 19.3217 16.713 19.0487 16.4775C18.7758 16.2419 18.6224 15.9224 18.6224 15.5893ZM8.51115 17.6605C8.32001 17.6605 8.13075 17.693 7.95416 17.7561C7.77757 17.8193 7.61712 17.9118 7.48197 18.0284C7.34681 18.1451 7.2396 18.2835 7.16646 18.4359C7.09331 18.5883 7.05566 18.7516 7.05566 18.9166C7.05566 19.0815 7.09331 19.2449 7.16646 19.3973C7.2396 19.5496 7.34681 19.6881 7.48197 19.8047C7.61712 19.9214 7.77757 20.0139 7.95416 20.077C8.13075 20.1401 8.32001 20.1726 8.51115 20.1726H16.2223C16.4135 20.1726 16.6027 20.1401 16.7793 20.077C16.9559 20.0139 17.1164 19.9214 17.2515 19.8047C17.3867 19.6881 17.4939 19.5496 17.567 19.3973C17.6402 19.2449 17.6778 19.0815 17.6778 18.9166C17.6778 18.7516 17.6402 18.5883 17.567 18.4359C17.4939 18.2835 17.3867 18.1451 17.2515 18.0284C17.1164 17.9118 16.9559 17.8193 16.7793 17.7561C16.6027 17.693 16.4135 17.6605 16.2223 17.6605H8.51115Z"
                  fill="#ED2D20"
                />
                <path
                  d="M28.5003 25.7917C28.5003 25.5486 28.4037 25.3154 28.2318 25.1435C28.0599 24.9716 27.8268 24.875 27.5837 24.875C27.3405 24.875 27.1074 24.9716 26.9355 25.1435C26.7636 25.3154 26.667 25.5486 26.667 25.7917V28.4628L27.8522 29.6481C28.0251 29.8151 28.2567 29.9075 28.497 29.9054C28.7374 29.9033 28.9673 29.8069 29.1372 29.6369C29.3072 29.467 29.4036 29.237 29.4057 28.9967C29.4078 28.7564 29.3154 28.5248 29.1484 28.3519L28.5003 27.7038V25.7917Z"
                  fill="#53100B"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.5 2.41667C6.5 2.17355 6.59658 1.94039 6.76849 1.76849C6.94039 1.59658 7.17355 1.5 7.41667 1.5C7.65978 1.5 7.89294 1.59658 8.06485 1.76849C8.23676 1.94039 8.33333 2.17355 8.33333 2.41667V7C8.33333 7.24311 8.42991 7.47627 8.60182 7.64818C8.77373 7.82009 9.00688 7.91667 9.25 7.91667C9.49311 7.91667 9.72627 7.82009 9.89818 7.64818C10.0701 7.47627 10.1667 7.24311 10.1667 7V4.25H19.3333V2.41667C19.3333 2.17355 19.4299 1.94039 19.6018 1.76849C19.7737 1.59658 20.0069 1.5 20.25 1.5C20.4931 1.5 20.7263 1.59658 20.8982 1.76849C21.0701 1.94039 21.1667 2.17355 21.1667 2.41667V7C21.1667 7.24311 21.2632 7.47627 21.4352 7.64818C21.6071 7.82009 21.8402 7.91667 22.0833 7.91667C22.3264 7.91667 22.5596 7.82009 22.7315 7.64818C22.9034 7.47627 23 7.24311 23 7V4.25H25.75C26.4793 4.25 27.1788 4.53973 27.6945 5.05546C28.2103 5.57118 28.5 6.27065 28.5 7V21.7308C30.1056 21.9629 31.5637 22.7944 32.581 24.0581C33.5983 25.3218 34.0992 26.9238 33.9831 28.5419C33.8669 30.16 33.1423 31.6741 31.9549 32.7795C30.7675 33.8849 29.2056 34.4997 27.5833 34.5C26.5487 34.5008 25.5293 34.2511 24.6122 33.7722C23.6951 33.2933 22.9076 32.5995 22.3171 31.75H3.75C3.02065 31.75 2.32118 31.4603 1.80546 30.9445C1.28973 30.4288 1 29.7293 1 29V7C1 6.27065 1.28973 5.57118 1.80546 5.05546C2.32118 4.53973 3.02065 4.25 3.75 4.25H6.5V2.41667ZM21.1667 28.0833C21.1663 26.5401 21.7221 25.0483 22.7322 23.8816C23.7424 22.7149 25.1392 21.9513 26.6667 21.7308V12.5H2.83333V29C2.83333 29.2431 2.92991 29.4763 3.10182 29.6482C3.27373 29.8201 3.50688 29.9167 3.75 29.9167H21.4325C21.2557 29.3216 21.1661 28.7041 21.1667 28.0833ZM32.1667 28.0833C32.1667 29.2989 31.6838 30.4647 30.8242 31.3242C29.9647 32.1838 28.7989 32.6667 27.5833 32.6667C26.3678 32.6667 25.202 32.1838 24.3424 31.3242C23.4829 30.4647 23 29.2989 23 28.0833C23 26.8678 23.4829 25.702 24.3424 24.8424C25.202 23.9829 26.3678 23.5 27.5833 23.5C28.7989 23.5 29.9647 23.9829 30.8242 24.8424C31.6838 25.702 32.1667 26.8678 32.1667 28.0833Z"
                  fill="#53100B"
                />
              </svg>
              <button
                className="flex flex-col items-start min-w-0 outline-none"
                onClick={() => {
                  if (pickupDate) setCalMonth(pickupDate.startOf("month"));
                  setActivePanel((p) => (p === "pickup" ? null : "pickup"));
                }}
              >
                <span className="text-[#9CA3AF] text-xs font-normal">Pick Up</span>
                <span className="text-[#111827] text-sm font-semibold font-['Outfit'] whitespace-nowrap">
                  {pickupDate ? pickupDate.format("D MMM YYYY") : "Select pickup date"}
                  {pickupTime ? <span className="text-[#6b7280] font-normal"> · {dayjs(`2000-01-01 ${pickupTime}`).format("h:mm A")}</span> : <span className="text-[#6b7280] font-normal"> · Select pickup time</span>}
                </span>
              </button>
            </div>

            {/* Duration Badge (in between) */}
            <div className="px-2.5 py-1 bg-[#F1F5F9] rounded-full inline-flex items-center gap-1.5 shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#111827]"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="text-black text-xs font-semibold font-['Outfit']">
                {subscriptionDays * 24} Hrs
              </span>
            </div>

            {/* Drop Off Section */}
            <div className="flex items-center gap-2.5 min-w-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 36 36"
                fill="none"
                className="shrink-0"
              >
                <path
                  d="M16.2127 14.3333C15.8292 14.3333 15.4615 14.4647 15.1903 14.6987C14.9192 14.9327 14.7668 15.2501 14.7668 15.581V15.5976C14.7668 16.2864 15.4146 16.8454 16.2127 16.8454H16.232C16.6154 16.8454 16.9832 16.7139 17.2543 16.4799C17.5255 16.2459 17.6778 15.9285 17.6778 15.5976V15.581C17.6778 15.2501 17.5255 14.9327 17.2543 14.6987C16.9832 14.4647 16.6154 14.3333 16.232 14.3333H16.2127ZM8.50151 20.9878C8.11805 20.9878 7.75029 21.1193 7.47914 21.3533C7.20799 21.5873 7.05566 21.9046 7.05566 22.2355V22.2522C7.05566 22.9409 7.7034 23.4999 8.50151 23.4999H8.52079C8.90425 23.4999 9.27201 23.3685 9.54316 23.1345C9.8143 22.9005 9.96664 22.5831 9.96664 22.2522V22.2355C9.96664 21.9046 9.8143 21.5873 9.54316 21.3533C9.27201 21.1193 8.90425 20.9878 8.52079 20.9878H8.50151ZM12.3571 20.9878C11.9736 20.9878 11.6059 21.1193 11.3347 21.3533C11.0636 21.5873 10.9113 21.9046 10.9113 22.2355V22.2522C10.9113 22.9409 11.559 23.4999 12.3571 23.4999H12.3764C12.7598 23.4999 13.1276 23.3685 13.3987 23.1345C13.6699 22.9005 13.8222 22.5831 13.8222 22.2522V22.2355C13.8222 21.9046 13.6699 21.5873 13.3987 21.3533C13.1276 21.1193 12.7598 20.9878 12.3764 20.9878H12.3571ZM14.7668 22.2355C14.7668 21.9046 14.9192 21.5873 15.1903 21.3533C15.4615 21.1193 15.8292 20.9878 16.2127 20.9878H16.232C16.6154 20.9878 16.9832 21.1193 17.2543 21.3533C17.5255 21.5873 17.6778 21.9046 17.6778 22.2355V22.2522C17.6778 22.5831 17.5255 22.9005 17.2543 23.1345C16.9832 23.3685 16.6154 23.4999 16.232 23.4999H16.2127C15.8292 23.4999 15.4615 23.3685 15.1903 23.1345C14.9192 22.9005 14.7668 22.5831 14.7668 22.2522V22.2355ZM20.0683 17.6605C19.6848 17.6605 19.3171 17.792 19.0459 18.026C18.7748 18.26 18.6224 18.5773 18.6224 18.9083V18.9249C18.6224 19.6137 19.2702 20.1726 20.0683 20.1726H20.0876C20.471 20.1726 20.8388 20.0412 21.1099 19.8072C21.3811 19.5732 21.5334 19.2558 21.5334 18.9249V18.9083C21.5334 18.5773 21.3811 18.26 21.1099 18.026C20.8388 17.792 20.471 17.6605 20.0876 17.6605H20.0683ZM20.0683 20.9878C19.6848 20.9878 19.3171 21.1193 19.0459 21.3533C18.7748 21.5873 18.6224 21.9046 18.6224 22.2355V22.2522C18.6224 22.9409 19.2702 23.4999 20.0683 23.4999H20.0876C20.471 23.4999 20.8388 23.3685 21.1099 23.1345C21.3811 22.9005 21.5334 22.5831 21.5334 22.2522V22.2355C21.5334 21.9046 21.3811 21.5873 21.1099 21.3533C20.8388 21.1193 20.471 20.9878 20.0876 20.9878H20.0683ZM22.478 18.9083C22.478 18.5773 22.6304 18.26 22.9015 18.026C23.1727 17.792 23.5404 17.6605 23.9239 17.6605H23.9432C24.3266 17.6605 24.6944 17.792 24.9655 18.026C25.2367 18.26 25.389 18.5773 25.389 18.9083V18.9249C25.389 19.2558 25.2367 19.5732 24.9655 19.8072C24.6944 20.0412 24.3266 20.1726 23.9432 20.1726H23.9239C23.5404 20.1726 23.1727 20.0412 22.9015 19.8072C22.6304 19.5732 22.478 19.2558 22.478 18.9249V18.9083ZM18.6224 15.5893C18.6224 14.8956 19.274 14.3333 20.0779 14.3333H23.9335C24.3195 14.3333 24.6897 14.4656 24.9627 14.7011C25.2357 14.9367 25.389 15.2562 25.389 15.5893C25.389 15.9224 25.2357 16.2419 24.9627 16.4775C24.6897 16.713 24.3195 16.8454 23.9335 16.8454H20.0779C19.6919 16.8454 19.3217 16.713 19.0487 16.4775C18.7758 16.2419 18.6224 15.9224 18.6224 15.5893ZM8.51115 17.6605C8.32001 17.6605 8.13075 17.693 7.95416 17.7561C7.77757 17.8193 7.61712 17.9118 7.48197 18.0284C7.34681 18.1451 7.2396 18.2835 7.16646 18.4359C7.09331 18.5883 7.05566 18.7516 7.05566 18.9166C7.05566 19.0815 7.09331 19.2449 7.16646 19.3973C7.2396 19.5496 7.34681 19.6881 7.48197 19.8047C7.61712 19.9214 7.77757 20.0139 7.95416 20.077C8.13075 20.1401 8.32001 20.1726 8.51115 20.1726H16.2223C16.4135 20.1726 16.6027 20.1401 16.7793 20.077C16.9559 20.0139 17.1164 19.9214 17.2515 19.8047C17.3867 19.6881 17.4939 19.5496 17.567 19.3973C17.6402 19.2449 17.6778 19.0815 17.6778 18.9166C17.6778 18.7516 17.6402 18.5883 17.567 18.4359C17.4939 18.2835 17.3867 18.1451 17.2515 18.0284C17.1164 17.9118 16.9559 17.8193 16.7793 17.7561C16.6027 17.693 16.4135 17.6605 16.2223 17.6605H8.51115Z"
                  fill="#ED2D20"
                />
                <path
                  d="M28.5003 25.7917C28.5003 25.5486 28.4037 25.3154 28.2318 25.1435C28.0599 24.9716 27.8268 24.875 27.5837 24.875C27.3405 24.875 27.1074 24.9716 26.9355 25.1435C26.7636 25.3154 26.667 25.5486 26.667 25.7917V28.4628L27.8522 29.6481C28.0251 29.8151 28.2567 29.9075 28.497 29.9054C28.7374 29.9033 28.9673 29.8069 29.1372 29.6369C29.3072 29.467 29.4036 29.237 29.4057 28.9967C29.4078 28.7564 29.3154 28.5248 29.1484 28.3519L28.5003 27.7038V25.7917Z"
                  fill="#53100B"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.5 2.41667C6.5 2.17355 6.59658 1.94039 6.76849 1.76849C6.94039 1.59658 7.17355 1.5 7.41667 1.5C7.65978 1.5 7.89294 1.59658 8.06485 1.76849C8.23676 1.94039 8.33333 2.17355 8.33333 2.41667V7C8.33333 7.24311 8.42991 7.47627 8.60182 7.64818C8.77373 7.82009 9.00688 7.91667 9.25 7.91667C9.49311 7.91667 9.72627 7.82009 9.89818 7.64818C10.0701 7.47627 10.1667 7.24311 10.1667 7V4.25H19.3333V2.41667C19.3333 2.17355 19.4299 1.94039 19.6018 1.76849C19.7737 1.59658 20.0069 1.5 20.25 1.5C20.4931 1.5 20.7263 1.59658 20.8982 1.76849C21.0701 1.94039 21.1667 2.17355 21.1667 2.41667V7C21.1667 7.24311 21.2632 7.47627 21.4352 7.64818C21.6071 7.82009 21.8402 7.91667 22.0833 7.91667C22.3264 7.91667 22.5596 7.82009 22.7315 7.64818C22.9034 7.47627 23 7.24311 23 7V4.25H25.75C26.4793 4.25 27.1788 4.53973 27.6945 5.05546C28.2103 5.57118 28.5 6.27065 28.5 7V21.7308C30.1056 21.9629 31.5637 22.7944 32.581 24.0581C33.5983 25.3218 34.0992 26.9238 33.9831 28.5419C33.8669 30.16 33.1423 31.6741 31.9549 32.7795C30.7675 33.8849 29.2056 34.4997 27.5833 34.5C26.5487 34.5008 25.5293 34.2511 24.6122 33.7722C23.6951 33.2933 22.9076 32.5995 22.3171 31.75H3.75C3.02065 31.75 2.32118 31.4603 1.80546 30.9445C1.28973 30.4288 1 29.7293 1 29V7C1 6.27065 1.28973 5.57118 1.80546 5.05546C2.32118 4.53973 3.02065 4.25 3.75 4.25H6.5V2.41667ZM21.1667 28.0833C21.1663 26.5401 21.7221 25.0483 22.7322 23.8816C23.7424 22.7149 25.1392 21.9513 26.6667 21.7308V12.5H2.83333V29C2.83333 29.2431 2.92991 29.4763 3.10182 29.6482C3.27373 29.8201 3.50688 29.9167 3.75 29.9167H21.4325C21.2557 29.3216 21.1661 28.7041 21.1667 28.0833ZM32.1667 28.0833C32.1667 29.2989 31.6838 30.4647 30.8242 31.3242C29.9647 32.1838 28.7989 32.6667 27.5833 32.6667C26.3678 32.6667 25.202 32.1838 24.3424 31.3242C23.4829 30.4647 23 29.2989 23 28.0833C23 26.8678 23.4829 25.702 24.3424 24.8424C25.202 23.9829 26.3678 23.5 27.5833 23.5C28.7989 23.5 29.9647 23.9829 30.8242 24.8424C31.6838 25.702 32.1667 26.8678 32.1667 28.0833Z"
                  fill="#53100B"
                />
              </svg>
              <button
                className="flex flex-col items-start min-w-0 outline-none"
                onClick={() => {
                  if (dropDate) setCalMonth(dropDate.startOf("month"));
                  setActivePanel((p) => (p === "dropoff" ? null : "dropoff"));
                }}
              >
                <span className="text-[#9CA3AF] text-xs font-normal">Drop Off</span>
                <span className="text-[#111827] text-sm font-semibold font-['Outfit'] whitespace-nowrap">
                  {dropDate ? dropDate.format("D MMM YYYY") : "Select drop date"}
                  {dropTime ? <span className="text-[#6b7280] font-normal"> · {dayjs(`2000-01-01 ${dropTime}`).format("h:mm A")}</span> : <span className="text-[#6b7280] font-normal"> · Select drop time</span>}
                </span>
              </button>
            </div>

            </div>{/* end pick-up/pill/drop-off wrapper */}

            {/* Search Action Button */}
            <button
              onClick={getVehicles}
              aria-label="Search"
              className="size-12 rounded-full outline outline-[1.6px] outline-offset-[-0.8px] outline-indigo-50 inline-flex items-center justify-center shrink-0 shadow-md hover:opacity-95 transition"
              style={{ background: "linear-gradient(45deg, #9E85DD 25%, #7B39EC 59.86%)" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
              >
                <g clipPath="url(#clip0_209_2238)">
                  <path
                    d="M15.1185 1.87042C16.09 1.87039 17.2592 1.75661 18.1321 2.20929C18.5866 2.44349 18.9725 2.79168 19.2532 3.21906C19.4786 3.56635 19.601 3.91495 19.7288 4.30499C20.0501 5.2853 20.3902 6.26168 20.7034 7.24445C21.0356 7.04613 21.3987 6.76495 21.7903 6.86749C21.9905 6.92204 22.16 7.05585 22.2601 7.23761C22.3556 7.40447 22.3808 7.60309 22.3294 7.78839C22.2109 8.20673 21.5746 8.48021 21.1937 8.69269C21.2365 8.85296 21.262 8.9308 21.3265 9.08332C22.1143 9.60822 22.635 10.1053 22.9925 11.0081C23.2924 11.7655 23.2554 12.4797 23.2503 13.2737C22.9832 12.8489 22.589 12.4275 22.2112 12.0989C22.0288 11.9403 21.8098 11.7841 21.64 11.6126C21.5928 11.565 21.4905 11.3441 21.4525 11.2718C21.3592 11.0955 21.2391 10.9342 21.097 10.7943C20.2774 9.97182 19.2959 10.1275 18.2288 10.1273L15.3753 10.1263L5.07159 10.1253C4.20515 10.1261 3.55961 10.1371 2.90069 10.7962C2.23352 11.4638 2.25176 12.1811 2.25128 13.053L2.25226 14.3089V16.5384C2.25667 17.0819 2.27704 17.5187 2.68878 17.9319C3.48224 18.7282 4.8901 18.3784 5.18585 17.2884C5.30016 16.8669 5.13082 16.3042 5.4671 15.971C5.56931 15.8698 5.69748 15.7922 5.84405 15.7757C6.46367 15.7236 7.13277 15.7454 7.75323 15.7454L11.7181 15.7444C11.6404 16.1401 11.6367 16.8429 11.6429 17.2503C10.0315 17.2774 8.34447 17.2524 6.72686 17.2523C6.71355 17.3398 6.6987 17.4272 6.68097 17.514C6.52101 18.276 6.03101 18.9718 5.37921 19.3948C4.6992 19.8275 3.87551 19.9736 3.08819 19.8011C2.32822 19.6363 1.63757 19.143 1.21905 18.4896C1.00605 18.1593 0.863691 17.7885 0.800107 17.4007C0.711281 16.8389 0.747655 15.5841 0.747373 14.9446L0.74542 13.2816C0.745222 12.7447 0.724648 12.0401 0.847959 11.5267C1.11804 10.403 1.71549 9.70208 2.66241 9.09992C2.71017 9.00142 2.76165 8.84945 2.80108 8.74347C2.40497 8.48563 1.6972 8.23848 1.59112 7.75324C1.54846 7.5561 1.58615 7.3494 1.69561 7.17999C1.80345 7.01413 1.97546 6.90028 2.17022 6.86554C2.56356 6.79203 2.96377 7.07863 3.27569 7.29328L4.02765 5.03546C4.16145 4.63236 4.36351 3.96607 4.53351 3.60382C4.65018 3.35265 4.80103 3.11839 4.98175 2.90851C5.5493 2.25142 6.25444 1.94689 7.10675 1.8841C7.48303 1.84816 8.30582 1.87034 8.70147 1.87042L11.7776 1.8714L15.1185 1.87042ZM8.78839 3.37628C8.36656 3.37621 7.66751 3.35566 7.26593 3.38507C6.0174 3.46633 5.87227 4.23737 5.53351 5.27082C5.48424 5.42107 5.43129 5.57835 5.38116 5.72882L4.41827 8.61652C5.8937 8.63393 7.38614 8.62145 8.86358 8.6214H16.7689C17.6894 8.62142 18.6665 8.63786 19.5823 8.61749L18.6292 5.7591C18.453 5.22902 18.2681 4.55975 18.011 4.07355C17.781 3.63887 17.2152 3.37083 16.7405 3.38507C16.148 3.37444 15.5594 3.37605 14.9671 3.37628L11.7005 3.37726L8.78839 3.37628Z"
                    fill="white"
                  />
                  <path
                    d="M17.7303 12.3849C18.6313 12.2845 19.7539 12.6482 20.4959 13.1602C21.4703 13.83 22.138 14.8605 22.3514 16.0235C22.5746 17.242 22.3062 18.4103 21.6033 19.4249C21.8259 19.6075 23.0113 20.7918 23.1287 20.9757C23.202 21.0906 23.2421 21.2232 23.2449 21.3595C23.2568 21.7911 22.9266 22.1191 22.4979 22.1222C22.3655 22.1232 22.2346 22.0862 22.1219 22.0167C21.9194 21.8912 20.8465 20.7855 20.6072 20.547L20.5633 20.5011C20.5299 20.5043 19.9 20.8671 19.785 20.9181C19.3015 21.1283 18.7854 21.2559 18.2596 21.294C17.0891 21.3731 15.9345 20.9868 15.0467 20.2198C14.1599 19.4587 13.611 18.3771 13.5203 17.212C13.338 14.6463 15.1673 12.5572 17.7303 12.3849ZM20.8983 16.4513C20.6838 14.8318 19.1976 13.6921 17.5779 13.9054C15.9569 14.119 14.8165 15.6077 15.0311 17.2286C15.2459 18.8494 16.7346 19.9892 18.3553 19.7735C19.9746 19.558 21.1126 18.0707 20.8983 16.4513Z"
                    fill="white"
                  />
                  <path
                    d="M5.55888 12.3824C6.01491 12.3569 7.23007 12.2837 7.5796 12.5277C7.73534 12.6365 7.83263 12.8024 7.86326 12.9888C7.8972 13.1922 7.84737 13.4007 7.72514 13.5668C7.57845 13.7676 7.4246 13.8297 7.1922 13.8685C6.75844 13.8959 5.50467 13.9542 5.17156 13.722C5.01448 13.6125 4.91792 13.447 4.88719 13.2592C4.85377 13.0568 4.90294 12.8495 5.02371 12.6837C5.17092 12.4812 5.32601 12.4202 5.55888 12.3824Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_209_2238">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>
          </div>

          {/* Subscription duration selector */}
          <div className="w-[95%] max-w-[1311px] flex items-center justify-between">
            <span className="text-[#252525] text-[22px] font-semibold">
              Subscription Duration
            </span>
            <div className="flex items-center gap-4">
              {durationOptions.map((opt) => {
                const active = subscriptionDays === opt.days;
                return (
                  <button
                    key={opt.days}
                    onClick={() => applyDuration(opt.days)}
                    style={active ? { background: "linear-gradient(43.88deg, #FFE4DD 13.648%, #ffffff 44.708%)" } : {}}
                    className={`w-[255px] h-[60px] px-4 rounded-xl flex items-center justify-between transition ${
                      active
                        ? "border-t border-l border-r border-b-4 border-[#ED2D20]"
                        : "bg-white border border-[#EAEAEA]"
                    }`}
                  >
                    <div className="flex flex-col items-start gap-[2px]">
                      <span
                        className={`text-base font-semibold leading-normal ${active ? "text-[#0f172a]" : "text-[#5E5E5E]"}`}
                      >
                        {opt.label}
                      </span>
                      <span
                        className={`text-[12px] font-medium tracking-[0.12px] ${active ? "text-[#adadad]" : "text-[#777777]"}`}
                      >
                        {opt.sub}
                      </span>
                    </div>
                    {active ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="10" cy="10" r="9" stroke="#ED2D20" strokeWidth="1.5" fill="white" />
                        <circle cx="10" cy="10" r="5" fill="#ED2D20" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="10" cy="10" r="9" stroke="#D3D3D3" strokeWidth="1.5" fill="white" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Calendar + Time picker panel ---------- */}
      {(() => {
        const disabledPickup = getDisabledPickupTimes();
        const disabledDrop   = getDisabledDropTimes();
        const calDays        = buildCalendarDays(calMonth);
        return (
          <AnimatePresence>
            {activePanel && (
            <motion.div
              key="dt-panel"
              ref={panelRef}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="w-full bg-white border-b border-[#e5e7eb] shadow-[0_4px_20px_rgba(0,0,0,0.08)] relative"
              style={{ zIndex: 100 }}
            >
              <div className="max-w-[900px] mx-auto px-6 py-5 flex gap-8">
                {/* Calendar */}
                <div className="flex-1 min-w-[300px]">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-[#0f172a] font-['Outfit']">{calMonth.format("MMMM YYYY")}</span>
                    <div className="flex gap-1">
                      <button onClick={() => setCalMonth((m) => m.subtract(1, "month"))} className="p-1.5 rounded-lg hover:bg-gray-100">
                        <ChevronLeft size={15} className="text-[#6b7280]" />
                      </button>
                      <button onClick={() => setCalMonth((m) => m.add(1, "month"))} className="p-1.5 rounded-lg hover:bg-gray-100">
                        <ChevronRight size={15} className="text-[#6b7280]" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 mb-1">
                    {DAY_LABELS.map((d) => (
                      <div key={d} className="text-center text-[11px] text-[#9ca3af] font-['Outfit'] py-1">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-y-0.5">
                    {calDays.map((day, i) => {
                      if (!day) return <div key={i} className="h-9" />;
                      const isPU    = pickupDate && day.isSame(pickupDate, "day");
                      const isDO    = dropDate   && day.isSame(dropDate, "day");
                      const inRange = pickupDate && dropDate && day.isAfter(pickupDate, "day") && day.isBefore(dropDate, "day");
                      let disabled  = day.isBefore(dayjs(), "day");
                      if (!disabled && activePanel === "dropoff" && pickupDate) {
                        disabled = day.isBefore(pickupDate, "day");
                      }
                      let bg = "transparent", color = disabled ? "#d1d5db" : "#374151";
                      if (isPU)      { bg = PURPLE_GRADIENT; color = "white"; }
                      else if (isDO) { bg = "#1e1b4b";       color = "white"; }
                      else if (inRange) { bg = PURPLE_LIGHT_BG; color = PURPLE_SOLID; }
                      return (
                        <button
                          key={i}
                          onClick={() => {
                            if (disabled) return;
                            if (activePanel === "pickup") {
                              setPickupDate(day);
                              const [ph, pm] = (pickupTime || "10:00").split(":").map(Number);
                              const drop = day.hour(ph).minute(pm).add(subscriptionDays, "day");
                              setDropDate(drop);
                              setDropTime(drop.format("HH:mm"));
                            } else {
                              setDropDate(day);
                            }
                          }}
                          className="h-9 w-9 mx-auto rounded-full text-sm font-['Outfit'] flex items-center justify-center transition-all"
                          style={{ background: bg, color, fontWeight: (isPU || isDO) ? 600 : 400, cursor: disabled ? "not-allowed" : "pointer", boxShadow: isPU ? PURPLE_SHADOW : "none" }}
                        >
                          {day.date()}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="w-px bg-[#f3f4f6]" />

                {/* Time grid */}
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock size={15} style={{ color: PURPLE_SOLID }} />
                    <span className="font-semibold text-[#0f172a] font-['Outfit']">
                      {activePanel === "pickup" ? "Select pickup time" : "Select drop time"}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 max-h-[300px] overflow-y-auto pr-1">
                    {ALL_SLOTS.map((slot) => {
                      const isActive   = activePanel === "pickup" ? slot.value === pickupTime : slot.value === dropTime;
                      const isDisabled = activePanel === "pickup" ? disabledPickup.has(slot.value) : disabledDrop.has(slot.value);
                      let btnBg = "white", btnColor = "#374151", btnBorder = "#e5e7eb";
                      if (isDisabled)   { btnBg = "#f3f4f6"; btnColor = "#d1d5db"; btnBorder = "#f3f4f6"; }
                      else if (isActive) {
                        btnBg    = activePanel === "pickup" ? PURPLE_GRADIENT : "#1e1b4b";
                        btnColor = "white";
                        btnBorder = "transparent";
                      }
                      return (
                        <button
                          key={slot.value}
                          disabled={isDisabled}
                          onClick={() => {
                            if (activePanel === "pickup") {
                              setPickupTime(slot.value);
                              const [ph, pm] = slot.value.split(":").map(Number);
                              const drop = pickupDate.hour(ph).minute(pm).add(subscriptionDays, "day");
                              setDropDate(drop);
                              setDropTime(drop.format("HH:mm"));
                            } else {
                              setDropTime(slot.value);
                              setActivePanel(null);
                            }
                          }}
                          className="py-2 rounded-xl text-[11px] font-['Outfit'] border transition-all"
                          style={{ background: btnBg, color: btnColor, borderColor: btnBorder, cursor: isDisabled ? "not-allowed" : "pointer" }}
                        >
                          {slot.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setActivePanel(null)}
                  className="absolute top-3 right-4 text-[#9ca3af] hover:text-[#374151] text-xl font-bold"
                  style={{ position: "absolute" }}
                >
                  ×
                </button>
              </div>
            </motion.div>
            )}
          </AnimatePresence>
        );
      })()}

      {/* ---------- Body: Filter sidebar + Listing (functionality from A, design from B) ---------- */}
      <div className="max-w-[1440px] mx-auto flex gap-6 px-6 py-10">
        {/* Filter sidebar */}
        <aside className="hidden lg:flex flex-col w-[280px] shrink-0">
          <div className="w-full bg-white rounded-[24px] border border-[#e0e0e0] flex flex-col font-sans overflow-hidden" style={{ maxHeight: "calc(100vh - 140px)", position: "sticky", top: "100px" }}>
            {/* Header */}
            <div className="w-full px-4 py-3 bg-white flex justify-between items-center shrink-0 relative z-20 shadow-[0_4px_6px_-6px_rgba(0,0,0,0.1)]">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6.66667 5.33333H9.33333M8 14V8M8 5.33333V2M11.3333 10.6667H14M12.6667 8V2M12.6667 14V10.6667M2 9.33333H4.66667M3.33333 6.66667V2M3.33333 14V9.33333" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span className="text-[#374151] text-[18px] font-medium">Filter</span>
              </div>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setSelectedFilters({}); setSortOption("lowtohigh"); }}
                className="text-[#6b7280] text-[12px] font-medium underline hover:text-[#374151] transition-colors bg-transparent border-none cursor-pointer"
              >
                Clear All
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-0 px-4 py-2" style={{ scrollbarWidth: "thin", scrollbarColor: "#babdee #f3f4f6" }}>

              {/* Sort Row */}
              <div className="flex items-center gap-2 py-3">
                <span className="text-[13px] font-medium text-black whitespace-nowrap">Sort by :</span>
                <div className="flex-1 bg-white border border-[#e5e7eb] rounded-[8px] flex items-center justify-between px-3 py-1.5">
                  <span className="text-[13px] font-medium text-black">{sortOption === "lowtohigh" ? "Low to high" : "High to low"}</span>
                  <button
                    type="button"
                    onClick={() => setSortOption(sortOption === "lowtohigh" ? "hightolow" : "lowtohigh")}
                    className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: "linear-gradient(45deg, #9E85DD 25%, #7B39EC 59.86%)" }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                      <g clipPath="url(#clip0_sub_sort)">
                        <path d="M11.5005 0.5H12.0005C12.0005 0.367392 11.9478 0.240215 11.854 0.146447C11.7603 0.0526784 11.6331 0 11.5005 0V0.5ZM4.50048 14.5L4.14648 14.854C4.19293 14.9006 4.24811 14.9375 4.30885 14.9627C4.3696 14.9879 4.43472 15.0009 4.50048 15.0009C4.56625 15.0009 4.63137 14.9879 4.69212 14.9627C4.75286 14.9375 4.80804 14.9006 4.85448 14.854L4.50048 14.5ZM10.0005 1H11.5005V0L10.0005 0V1ZM11.0005 0.5V6.5H12.0005V0.5H11.0005ZM10.0005 7H13.0005V6H10.0005V7ZM11.8625 8H11.0005V9H11.8625V8ZM12.9745 11.658L13.4565 10.212L12.5085 9.896L12.0265 11.342L12.9745 11.658ZM11.0005 12H12.5005V11H11.0005V12ZM11.9745 14.658L12.9745 11.658L12.0265 11.342L11.0265 14.342L11.9745 14.658ZM9.00048 10C9.00048 10.5304 9.2112 11.0391 9.58627 11.4142C9.96134 11.7893 10.4701 12 11.0005 12V11C10.7353 11 10.4809 10.8946 10.2934 10.7071C10.1058 10.5196 10.0005 10.2652 10.0005 10H9.00048ZM11.0005 8C10.4701 8 9.96134 8.21071 9.58627 8.58579C9.2112 8.96086 9.00048 9.46957 9.00048 10H10.0005C10.0005 9.73478 10.1058 9.48043 10.2934 9.29289C10.4809 9.10536 10.7353 9 11.0005 9V8ZM11.8625 9C11.9704 8.99981 12.0769 9.02531 12.173 9.0744C12.2691 9.12349 12.3522 9.19476 12.4153 9.28232C12.4784 9.36987 12.5198 9.47119 12.536 9.5779C12.5522 9.68462 12.5428 9.79365 12.5085 9.896L13.4565 10.212C13.5408 9.9594 13.5639 9.69037 13.5238 9.4271C13.4837 9.16384 13.3816 8.91387 13.2259 8.69782C13.0702 8.48176 12.8654 8.30582 12.6284 8.18449C12.3913 8.06316 12.1288 7.99993 11.8625 8V9ZM4.85448 14.854L7.85448 11.854L7.14648 11.146L4.14648 14.146L4.85448 14.854ZM4.85448 14.146L1.85448 11.146L1.14648 11.854L4.14648 14.854L4.85448 14.146ZM4.00048 0V14.5H5.00048V0H4.00048Z" fill="white"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_sub_sort"><rect width="15" height="15" fill="white"/></clipPath>
                      </defs>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="w-full h-px bg-neutral-200 shrink-0" />

              {/* Vehicle Type — image card grid */}
              <div className="flex flex-col gap-2 py-3">
                <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection("VehicleType")}>
                  <span className="text-[#1a1917] text-[14px] font-semibold tracking-[0.4px]">Vehicle Type</span>
                  <ChevronDown size={15} className={`text-[#374151] transition-transform ${openSections["VehicleType"] === false ? "" : "rotate-180"}`} />
                </div>
                {openSections["VehicleType"] !== false && (
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: "Hatchback",     key: "Hatchback",     img: "/assets/collection/hatchback1.png" },
                      { label: "Sedan",         key: "Sedan",         img: "/assets/collection/sedan1.png" },
                      { label: "SUV",           key: "SUV",           img: "/assets/collection/suv1.png" },
                      { label: "Crossover SUV", key: "Crossover SUV", img: "/assets/collection/crossover-suv1.png" },
                      { label: "Compact Sedan", key: "Compact Sedan", img: "/assets/collection/compact-sedan1.png" },
                      { label: "MUV",           key: "MUV",           img: "/assets/collection/muv1.png" },
                    ].map(({ label, key, img }) => {
                      const isSelected = (selectedFilters["Vehicle Body Type"] || []).includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => handleCheckbox("Vehicle Body Type", key)}
                          className={`relative flex flex-col items-center justify-end pb-2 rounded-[10px] border overflow-hidden transition-all h-[100px] ${
                            isSelected ? "border-[#e8273c] bg-[#fff5f6]" : "border-[#e5e7eb] bg-white hover:border-[#9ca3af]"
                          }`}
                        >
                          {isSelected && (
                            <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#e8273c] rounded-full flex items-center justify-center">
                              <svg width="9" height="7" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                          )}
                          <div className="flex-1 flex items-center justify-center w-full px-2 pt-2">
                            <img src={img} alt={label} className="w-full h-[58px] object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                          </div>
                          <span className={`text-[11px] font-medium leading-none text-center px-1 mt-1 ${isSelected ? "text-[#e8273c]" : "text-[#374151]"}`}>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="w-full h-px bg-neutral-200 shrink-0" />

              {/* Seating Capacity */}
              <div className="flex flex-col gap-2 py-3">
                <span className="text-[#1a1917] text-[14px] font-semibold tracking-[0.4px]">Seating Capacity</span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[4, 5, 6, 7].map((seat) => {
                    const isSelected = (selectedFilters["Seater"] || []).includes(String(seat));
                    return (
                      <button key={seat} type="button" onClick={() => handleCheckbox("Seater", String(seat))}
                        className={`h-[30px] px-2.5 rounded-[8px] border text-[12px] font-medium transition-all whitespace-nowrap ${
                          isSelected ? "bg-[rgba(184,177,251,0.32)] border-[#babdee] text-[#7f1d1d] font-bold" : "bg-white border-[#e5e5e5] text-[#1a1a1a] hover:border-[#babdee]"
                        }`}>
                        {seat} <span className="text-[10px]">Seats</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="w-full h-px bg-neutral-200 shrink-0" />

              {/* Transmission Type */}
              <div className="flex flex-col gap-2 py-3">
                <span className="text-[#1a1917] text-[14px] font-semibold tracking-[0.4px]">Transmission Type</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {["Automatic", "Manual"].map((type) => {
                    const isSelected = (selectedFilters["Transmission Type"] || []).includes(type);
                    return (
                      <button key={type} type="button" onClick={() => handleCheckbox("Transmission Type", type)}
                        className={`h-[32px] min-w-[90px] px-3 rounded-[8px] border text-[12px] font-medium transition-all ${
                          isSelected ? "bg-[rgba(184,177,251,0.32)] border-[#babdee] text-[#7f1d1d] font-bold" : "bg-white border-[#e5e5e5] text-[#1a1a1a] hover:border-[#babdee]"
                        }`}>
                        {type}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="w-full h-px bg-neutral-200 shrink-0" />

              {/* Fuel Type */}
              <div className="flex flex-col gap-2 py-3">
                <span className="text-[#1a1917] text-[14px] font-semibold tracking-[0.4px]">Fuel Type</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {["Petrol", "Diesel"].map((fuel) => {
                    const isSelected = (selectedFilters["Fuel Type"] || []).includes(fuel);
                    return (
                      <button key={fuel} type="button" onClick={() => handleCheckbox("Fuel Type", fuel)}
                        className={`h-[32px] min-w-[70px] px-3 rounded-[8px] border text-[12px] font-medium transition-all ${
                          isSelected ? "bg-[rgba(184,177,251,0.32)] border-[#babdee] text-[#7f1d1d] font-bold" : "bg-white border-[#e5e5e5] text-[#1a1a1a] hover:border-[#babdee]"
                        }`}>
                        {fuel}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="h-4 w-full shrink-0" />
            </div>
          </div>
        </aside>

        {/* Listing */}
        <main className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#6B7280]">
              Loading vehicles…
            </div>
          ) : carData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <img src="/assets/no-results.png" alt="No vehicles found" className="w-[469px] h-[312px] object-contain" />
              <span className="text-[#6B7280] text-base font-medium font-['Outfit']">No vehicles available for this search.</span>
            </div>
          ) : carData.every((car) => car.isSoldOut) ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <img src="/assets/no-results.png" alt="All sold out" className="w-[469px] h-[312px] object-contain" />
              <span className="text-[#111827] text-[16px] font-bold font-['Outfit'] text-center max-w-[420px]">
                Sorry all cars are sold out please try a different date or combination of filters
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {carData.map((car) => (
                <div
                  key={car.id}
                  className="p-4 bg-white shadow-[0px_8px_26px_-11px_rgba(212,184,184,0.07)] rounded-xl border border-[#E5E7EB] flex flex-col gap-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col gap-4 flex-1 min-w-0 items-start">
                      <div className="flex flex-col gap-2 items-start w-full">
                        <span className="text-[#252525] text-[22px] font-semibold text-left">
                          {car.name}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-[#F9FAFB] rounded border border-[#F3F3F9] text-[#1D4ED8] text-xs font-semibold uppercase tracking-wide">
                            {car.bodyTypeText}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-2 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB] text-[#111827] text-[13px] font-semibold">
                          {car.seater} Seats
                        </span>
                        <span className="px-2 py-2 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB] text-[#111827] text-[13px] font-semibold">
                          {car.transmissionTypeText}
                        </span>
                        <span className="px-2 py-2 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB] text-[#111827] text-[13px] font-semibold">
                          {car.fuelTypeText}
                        </span>
                      </div>

                      <div className="flex flex-col gap-1 items-start w-full">
                        <span className="text-[#646464] text-[11px] font-medium tracking-wide text-left">
                          Starting from
                        </span>
                        <div className="text-left">
                          <span className="text-[#CD2525] text-[28px] font-bold">
                            ₹{car.totalRent?.toLocaleString("en-IN")}{" "}
                          </span>
                          <span className="text-[#646464] text-sm font-bold">
                            /{car.totalTripDurationInDays} Days
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="w-[180px] h-[160px] relative shrink-0">
                      <img
                        src={car.imageUrl}
                        alt={car.name}
                        className="w-full h-[130px] object-contain absolute top-4 left-0"
                      />
                      <div className="absolute top-0 right-0 px-2 py-0.5 bg-[#00A18F] rounded text-white text-xs font-bold">
                        {car.totalTripDurationInDays}-Days Rental
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-[#ECFDF5] rounded-2xl border border-[#A7F3D0] flex items-center gap-3">
                    <div className="flex items-center gap-3">
                      <div className="px-2 py-2 bg-[#FF3636] rounded-lg">
                        <span className="text-white text-[15px] font-extrabold">
                          {car.totalFreeKms}Km
                        </span>
                      </div>
                      <span className="text-[#404040] text-base font-semibold">
                        Selected KM Package
                      </span>
                    </div>

                    <button
                      disabled={car.isSoldOut}
                      onClick={() => handleBooking(car)}
                      className={`h-8 px-3 rounded-lg flex items-center gap-2 font-medium text-[15px] ml-auto ${
                        car.isSoldOut
                          ? "bg-gray-200 text-gray-400"
                          : "text-white shadow-[0px_10.84px_26.02px_-6.5px_rgba(123,57,236,0.20)] border border-[#7B39EC]"
                      }`}
                      style={!car.isSoldOut ? { background: "linear-gradient(45deg, #9E85DD 25%, #7B39EC 59.86%)" } : undefined}
                    >
                      Book Now
                      {(car.isSoldOut || car.availableVehicleInCurrentGroup > 2) && (
                        <span className="w-11 h-6 bg-[#143D94] rounded shadow-inner flex items-center justify-center text-xs">
                          {car.isSoldOut ? "Sold" : `${car.availableVehicleInCurrentGroup} Left`}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

    </div>
  );
};

export default VehicleListing;